import { useState, useEffect } from 'react';
import { motion } from 'framer-motion';
import { artistsData } from '../../../mocks/artists';
import { useNavigate } from 'react-router-dom';
import { FavoriteButton } from '../../../components/feature/FavoriteButton';
import { useArtistGenreColors, findGenreColor } from '../../../hooks/useGenreColors';
import { supabase } from '../../../lib/supabase';

interface DbArtist {
  userId: string;
  name: string;
  location: string;
  genre: string;
  image: string;
  color: string;
  platforms: string[];
}

export function MakersSection() {
  const navigate = useNavigate();
  const genreColorMap = useArtistGenreColors();
  const [dbArtists, setDbArtists] = useState<DbArtist[]>([]);
  const [loading, setLoading] = useState(true);

  // Fetch registered artists from DB
  useEffect(() => {
    const fetchDbArtists = async () => {
      try {
        const { data: profiles, error: profileError } = await supabase
          .from('profiles')
          .select('id')
          .eq('user_type', 'maker');

        if (profileError) throw profileError;

        if (!profiles || profiles.length === 0) {
          setDbArtists([]);
          setLoading(false);
          return;
        }

        const userIds = profiles.map((p) => p.id);

        const { data: layers, error: layerError } = await supabase
          .from('artist_layers')
          .select('user_id, artist_name, genre, location, color, profile_image_url, socials, prefecture')
          .in('user_id', userIds);

        if (layerError) throw layerError;

        const results: DbArtist[] = (layers || []).map((layer) => {
          const platforms: string[] = [];
          if (layer.socials && Array.isArray(layer.socials)) {
            (layer.socials as { platform: string }[]).forEach((s) => {
              const name = (s.platform || '').toLowerCase();
              if (name.includes('instagram')) platforms.push('instagram');
              else if (name.includes('twitter') || name.includes('x')) platforms.push('twitter');
              else if (name.includes('youtube')) platforms.push('youtube');
              else if (name.includes('soundcloud')) platforms.push('soundcloud');
              else if (name.includes('spotify')) platforms.push('spotify');
              else if (name.includes('tiktok')) platforms.push('tiktok');
            });
          }

          return {
            userId: layer.user_id,
            name: layer.artist_name || 'UNKNOWN',
            location: layer.location || layer.prefecture || '',
            genre: layer.genre || '',
            image: layer.profile_image_url || '',
            color: layer.color || '#00ff41',
            platforms: platforms.slice(0, 3),
          };
        });

        setDbArtists(results);
      } catch (err) {
        console.error('Failed to fetch DB artists:', err);
      } finally {
        setLoading(false);
      }
    };

    fetchDbArtists();
  }, []);

  return (
    <section className="relative py-16 lg:py-32 bg-black overflow-hidden">
      {/* Background Noise */}
      <div
        className="absolute inset-0 opacity-5"
        style={{
          backgroundImage:
            'url("data:image/svg+xml,%3Csvg viewBox=\'0 0 400 400\' xmlns=\'http://www.w3.org/2000/svg\'%3E%3Cfilter id=\'noiseFilter\'%3E%3CfeTurbulence type=\'fractalNoise\' baseFrequency=\'0.9\' numOctaves=\'4\' /%3E%3C/filter%3E%3Crect width=\'100%25\' height=\'100%25\' filter=\'url(%23noiseFilter)\' /%3E%3C/svg%3E")',
        }}
      />

      {/* ── Fluid Ambient Glow ── */}
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 700,
          height: 700,
          background: 'radial-gradient(circle, rgba(255,0,110,0.16) 0%, transparent 70%)',
          filter: 'blur(80px)',
          top: '-15%',
          left: '-10%',
        }}
        animate={{
          x: [0, 80, -50, 0],
          y: [0, 60, -40, 0],
          scale: [1, 1.2, 0.88, 1],
        }}
        transition={{ duration: 24, repeat: Infinity, ease: 'easeInOut' }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 500,
          height: 500,
          background: 'radial-gradient(circle, rgba(255,0,110,0.10) 0%, transparent 70%)',
          filter: 'blur(100px)',
          bottom: '0%',
          right: '-5%',
        }}
        animate={{
          x: [0, -60, 40, 0],
          y: [0, -50, 30, 0],
          scale: [1, 0.85, 1.15, 1],
        }}
        transition={{ duration: 20, repeat: Infinity, ease: 'easeInOut', delay: 4 }}
      />
      <motion.div
        className="absolute rounded-full pointer-events-none"
        style={{
          width: 350,
          height: 350,
          background: 'radial-gradient(circle, rgba(200,0,80,0.08) 0%, transparent 70%)',
          filter: 'blur(120px)',
          top: '50%',
          left: '45%',
        }}
        animate={{
          x: [0, -70, 50, 0],
          y: [0, 50, -35, 0],
          scale: [1, 1.1, 0.9, 1],
        }}
        transition={{ duration: 28, repeat: Infinity, ease: 'easeInOut', delay: 8 }}
      />

      <div className="container mx-auto px-4 sm:px-8 relative z-10">
        {/* Section Title */}
        <div className="mb-10 lg:mb-20 relative">
          <div
            className="absolute -left-2 lg:-left-4 top-0 w-1 h-20 lg:h-32 bg-neon-pink"
            style={{
              boxShadow: '0 0 20px rgba(255, 0, 110, 0.9), 0 0 60px rgba(255, 0, 110, 0.3), 0 0 100px rgba(255, 0, 110, 0.1)',
            }}
          />
          <h2
            className="font-display text-4xl lg:text-7xl mb-3 lg:mb-6 tracking-wider"
            style={{
              color: '#ff006e',
              textShadow: '0 0 20px rgba(255,0,110,0.9), 0 0 40px rgba(255,0,110,0.5), 0 0 80px rgba(255,0,110,0.2)',
            }}
          >
            MAKERS
            <span
              className="block text-2xl lg:text-5xl mt-1 lg:mt-2"
              style={{
                color: '#ff006e',
                textShadow: '0 0 20px rgba(255,0,110,0.8), 0 0 40px rgba(255,0,110,0.4), 0 0 80px rgba(255,0,110,0.15)',
              }}
            >
              表現者たち
            </span>
          </h2>

          {/* Catchcopy */}
          <p
            className="text-white text-xl lg:text-3xl font-bold tracking-wide mb-4 lg:mb-6"
            style={{ textShadow: '0 0 24px rgba(255,0,110,0.5), 0 0 48px rgba(255,0,110,0.15)' }}
          >
            「上手さ」より「衝動」を
          </p>

          <p className="text-gray-400 text-sm lg:text-base max-w-3xl leading-relaxed">
            自室の静寂から街へと爪を立てる、実直な表現者。<br className="hidden sm:block" />
            奏者も裏方も、各地で『<span className="font-bold" style={{ color: '#ff006e', textShadow: '0 0 16px rgba(255, 0, 110, 0.9), 0 0 32px rgba(255, 0, 110, 0.3)' }}>体温のある音</span>』を鳴らし、誰かの心と衝突する瞬間を待つ。
          </p>
        </div>

        {/* Loading State */}
        {loading && (
          <div className="flex items-center justify-center py-16">
            <i className="ri-loader-4-line text-3xl text-neon-green animate-spin" />
          </div>
        )}

        {/* Artists Horizontal Scroll */}
        {!loading && (
          <div className="relative">
            <div
              className="flex gap-4 sm:gap-5 lg:gap-6 overflow-x-auto pb-4"
              style={{ scrollbarWidth: 'none', msOverflowStyle: 'none' }}
            >
              {/* DB Registered Artists */}
              {dbArtists.map((artist) => {
                const genreColor = findGenreColor(artist.genre, genreColorMap, artist.color);
                return (
                  <div
                    key={`db-${artist.userId}`}
                    className="flex-shrink-0 w-44 sm:w-56 lg:w-72 group cursor-pointer"
                    onClick={() => navigate(`/artist/u/${artist.userId}`)}
                  >
                    <div
                      className="relative bg-[#111] rounded-lg overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:shadow-[0_20px_60px_rgba(255,0,110,0.25)] flex flex-col h-full"
                      style={{
                        border: `1px solid ${artist.color}33`,
                      }}
                    >
                      <div className="relative w-full h-48 sm:h-64 lg:h-80 overflow-hidden flex-shrink-0">
                        {artist.image ? (
                          <img
                            src={artist.image}
                            alt={artist.name}
                            className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                          />
                        ) : (
                          <div
                            className="w-full h-full flex items-center justify-center"
                            style={{ background: `linear-gradient(135deg, ${artist.color}20, ${artist.color}05)` }}
                          >
                            <i className="ri-user-line text-5xl" style={{ color: `${artist.color}60` }} />
                          </div>
                        )}
                        <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-[#111] to-transparent" />
                        <div className="absolute top-2 left-2 px-2 py-0.5 rounded-full bg-neon-green/20 border border-neon-green/40">
                          <span className="text-neon-green text-[8px] font-bold tracking-wider">REGISTERED</span>
                        </div>
                        <div className="absolute top-2 right-2 z-10">
                          <FavoriteButton artistId={artist.userId} artistType="artist" size="sm" />
                        </div>
                      </div>
                      <div className="px-3 sm:px-4 lg:px-5 pt-3 lg:pt-4 pb-3 lg:pb-5 flex-1 flex flex-col">
                        <span className="text-[10px] lg:text-xs tracking-widest mb-1 lg:mb-2 block" style={{ color: artist.color }}>
                          {artist.location}
                        </span>
                        <div className="flex items-center justify-between mb-1 lg:mb-2">
                          <h3 className="text-white text-xs lg:text-sm font-bold tracking-wide">{artist.name}</h3>
                        </div>
                        <div className="w-8 lg:w-12 h-0.5 mb-2 lg:mb-3" style={{ background: artist.color, boxShadow: `0 0 10px ${artist.color}` }} />
                        <div className="mt-auto">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] lg:text-[10px] font-bold tracking-wider"
                            style={{ background: `${genreColor}15`, color: genreColor, border: `1px solid ${genreColor}30` }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }} />
                            {artist.genre || 'Artist'}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}

              {/* Mock Artists */}
              {artistsData.map((artist) => {
                const genreColor = findGenreColor(artist.genre, genreColorMap, artist.color);
                return (
                  <div
                    key={`mock-${artist.id}`}
                    className="flex-shrink-0 w-44 sm:w-56 lg:w-72 group cursor-pointer"
                    onClick={() => navigate(`/artist/${artist.id}`)}
                  >
                    <div
                      className="relative bg-[#111] rounded-lg overflow-hidden transition-all duration-500 hover:-translate-y-4 hover:shadow-[0_20px_60px_rgba(255,0,110,0.25)] flex flex-col h-full"
                      style={{ border: `1px solid ${artist.color}33` }}
                    >
                      <div className="relative w-full h-48 sm:h-64 lg:h-80 overflow-hidden flex-shrink-0">
                        <img
                          src={artist.image}
                          alt={artist.name}
                          className="w-full h-full object-cover object-top transition-transform duration-500 group-hover:scale-105"
                        />
                        <div className="absolute bottom-0 left-0 right-0 h-1/4 bg-gradient-to-t from-[#111] to-transparent" />
                        <div className="absolute top-2 right-2 z-10">
                          <FavoriteButton artistId={String(artist.id)} artistType="mock" size="sm" />
                        </div>
                      </div>
                      <div className="px-3 sm:px-4 lg:px-5 pt-3 lg:pt-4 pb-3 lg:pb-5 flex-1 flex flex-col">
                        <span className="text-[10px] lg:text-xs tracking-widest mb-1 lg:mb-2 block" style={{ color: artist.color }}>
                          {artist.location}
                        </span>
                        <div className="flex items-center justify-between mb-1 lg:mb-2">
                          <h3 className="text-white text-xs lg:text-sm font-bold tracking-wide">{artist.name}</h3>
                        </div>
                        <div className="w-8 lg:w-12 h-0.5 mb-2 lg:mb-3" style={{ background: artist.color, boxShadow: `0 0 10px ${artist.color}` }} />
                        <div className="mt-auto">
                          <span
                            className="inline-flex items-center gap-1 px-2 py-0.5 rounded-full text-[9px] lg:text-[10px] font-bold tracking-wider"
                            style={{ background: `${genreColor}15`, color: genreColor, border: `1px solid ${genreColor}30` }}
                          >
                            <span className="w-1.5 h-1.5 rounded-full flex-shrink-0" style={{ background: genreColor, boxShadow: `0 0 4px ${genreColor}` }} />
                            {artist.genre}
                          </span>
                        </div>
                      </div>
                    </div>
                  </div>
                );
              })}
            </div>

            {/* Right fade edge */}
            <div className="absolute right-0 top-0 bottom-4 w-16 bg-gradient-to-l from-black to-transparent pointer-events-none flex items-center justify-end pr-2">
              <i className="ri-arrow-right-s-line text-gray-600 text-2xl" />
            </div>
          </div>
        )}

        {/* View More */}
        <div className="mt-10 lg:mt-16 text-center">
          <a
            href="/directory"
            className="inline-flex items-center px-8 lg:px-10 py-3 lg:py-4 bg-transparent border-2 border-neon-green rounded-full text-neon-green font-bold text-sm lg:text-lg tracking-wider hover:-translate-y-1 hover:shadow-[0_10px_40px_rgba(0,255,65,0.4)] transition-all duration-300 whitespace-nowrap cursor-pointer"
          >
            もっと見る
            <i className="ri-arrow-right-line ml-2"></i>
          </a>
        </div>
      </div>
    </section>
  );
}
